import type { ReactNode } from 'react';
import type { LanguageMessage } from '../i18n';
import type { OrgRoute } from '../hooks/useRoute';
import {
  solarboxBikeControlSidebarEntryMetadata,
  solarboxBikeControlTileMetadata,
  solarboxSidebarEntryMetadata,
  solarboxTileMetadata,
} from './rakine/solarbox/surfaces';

/**
 * Display metadata each sidecar declares so `<AppSidecarTiles>` can
 * render a nav tile for it without the parent app having to know the
 * sidecar exists.
 *
 * `title` and `description` are language-message resolvers (rather
 * than plain strings) so the sidecar owns its own translation keys —
 * the parent doesn't pre-resolve them. `<AppSidecarTiles>` already
 * holds the active `messages` via `useLanguage()` and passes it down.
 *
 * `routePath` is the path *inside* the parent app (i.e. without the
 * `/<appId>/orgs/:orgId` prefix), e.g. `/solarbox/customers`. The
 * tile-rendering component composes the full URL by joining this with
 * `/<appId>/orgs/<orgId>`.
 */
export interface SidecarTileMetadata {
  id: string;
  title: (messages: LanguageMessage) => string;
  description?: (messages: LanguageMessage) => string | undefined;
  icon: ReactNode;
  routePath: string;
}

/**
 * Sidebar-entry display metadata, parallel to `SidecarTileMetadata`.
 * Read by `<AppSidecarSidebarEntries>` to inject a left-nav entry for
 * each enabled sidecar into the parent app's sidebar.
 *
 * `value` doubles as the `OrgRoute` literal that `useRoute` returns
 * as `activeLink` — keep them in sync so active-route highlighting
 * works (the sidecar must also add a path-detection branch in
 * `getActiveLink`).
 *
 * `iconName` is a string key into `SidebarNavItems`'s `ICON_MAP`
 * (rather than a `ReactNode`) so the sidebar can apply its own
 * uniform sizing/Tooltip wrapping. Adding a new sidecar with a new
 * icon requires extending `ICON_MAP` to register the icon — that's
 * intentional: it makes the sidebar's icon set explicit and
 * tree-shakable. Sidebar entries can reuse existing icon keys when
 * appropriate.
 *
 * `routePath` follows the same convention as `SidecarTileMetadata` —
 * relative to the parent app, no `/<appId>/orgs/:orgId` prefix.
 */
export interface SidecarSidebarEntryMetadata {
  value: OrgRoute;
  label: (messages: LanguageMessage) => string;
  iconName: string;
  routePath: string;
}

/**
 * Registry of per-sidecar tile metadata, keyed by `(appId, sidecarKey)`.
 *
 * Value is an ARRAY of `SidecarTileMetadata` so a single sidecar can
 * surface multiple tiles (e.g. Solarbox has both a "Paying customers"
 * view and a "Bike control" admin page — both gated by the same
 * `org_app_sidecars` row but rendered as peer tiles). Within an array
 * the order is the render order; across sidecars the array is sorted
 * alphabetically by sidecarKey in `useAppSidecarTiles`.
 *
 * Adding a new sidecar tile is a one-line change here PLUS a new
 * `surfaces.tsx` export in the sidecar's own directory — the metadata
 * itself stays self-contained per sidecar; this file just exists so
 * `<AppSidecarTiles>` has a single lookup table.
 *
 * Mirrors the backend's sidecar registry in `server/src/sidecars/index.ts`
 * (same `(appId, sidecarKey)` keying) so the two stay symmetrical.
 */
export const SIDECAR_TILE_REGISTRY: Record<
  string,
  Record<string, SidecarTileMetadata[]>
> = {
  rakine: {
    solarbox: [solarboxTileMetadata, solarboxBikeControlTileMetadata],
  },
};

/**
 * Sidebar-entry registry, parallel to `SIDECAR_TILE_REGISTRY`. Same
 * `(appId, sidecarKey)` shape — `<AppSidecarSidebarEntries>` reads
 * from this; `<AppSidecarTiles>` reads from the tiles registry. Two
 * registries (rather than one combined `surfaces` registry) so each
 * surface can be added/removed independently — a sidecar that wants
 * a sidebar entry but no tile can register here without nullable
 * fields, and the parent components don't need to filter `undefined`
 * out at render time.
 */
export const SIDECAR_SIDEBAR_ENTRY_REGISTRY: Record<
  string,
  Record<string, SidecarSidebarEntryMetadata[]>
> = {
  rakine: {
    solarbox: [
      solarboxSidebarEntryMetadata,
      solarboxBikeControlSidebarEntryMetadata,
    ],
  },
};

/**
 * Lookup helper used by `<AppSidecarTiles>` to find the metadata for a
 * given `(appId, sidecarKey)`. Returns an empty array when no metadata
 * is registered — the component treats that as "skip this sidecar in
 * the tile list" (the sidecar still works at the route level; it just
 * doesn't surface a tile). Always returns an array (not nullable) so
 * callers can `flatMap` without a guard.
 */
export const getSidecarTileMetadata = (
  appId: string,
  sidecarKey: string
): SidecarTileMetadata[] => SIDECAR_TILE_REGISTRY[appId]?.[sidecarKey] ?? [];

/**
 * Sidebar-entry counterpart to `getSidecarTileMetadata`. Same
 * "missing → empty" semantics so `<AppSidecarSidebarEntries>` can
 * flatMap without conditional logic at the call site.
 */
export const getSidecarSidebarEntryMetadata = (
  appId: string,
  sidecarKey: string
): SidecarSidebarEntryMetadata[] =>
  SIDECAR_SIDEBAR_ENTRY_REGISTRY[appId]?.[sidecarKey] ?? [];
